'use client';

import { useState, useEffect, useCallback } from 'react';
import { useEmailValidation } from './use-email-validation';
import { useDebounce } from './use-debounce';

interface ValidationRules {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: RegExp;
  custom?: (value: any) => boolean;
  isEmail?: boolean;
}

export function useFormValidation<T extends { [key: string]: any }>(
  initialValues: T,
  validationRules: { [K in keyof T]?: ValidationRules }
) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<{ [K in keyof T]?: string }>({});
  const [touched, setTouched] = useState<{ [K in keyof T]?: boolean }>({});
  const { checkEmail, emailExists, isChecking } = useEmailValidation();
  const debouncedEmail = useDebounce(values.email, 500);

  useEffect(() => {
    if (debouncedEmail && touched.email) {
      checkEmail(debouncedEmail);
    }
  }, [debouncedEmail, touched.email, checkEmail]);

  const validateField = useCallback(async (name: keyof T, value: any) => {
    const rules = validationRules[name];
    if (!rules) return '';

    if (rules.required && !value) {
      return 'Este campo é obrigatório';
    }

    if (value) {
      if (rules.minLength && value.length < rules.minLength) {
        return `Mínimo de ${rules.minLength} caracteres`;
      }

      if (rules.maxLength && value.length > rules.maxLength) {
        return `Máximo de ${rules.maxLength} caracteres`;
      }

      if (rules.min && Number(value) < rules.min) {
        return `Valor mínimo é ${rules.min}`;
      }

      if (rules.max && Number(value) > rules.max) {
        return `Valor máximo é ${rules.max}`;
      }

      if (rules.pattern && !rules.pattern.test(value)) {
        return 'Formato inválido';
      }

      if (rules.isEmail) {
        if (emailExists) {
          return 'Este email já está cadastrado';
        }
      }
    }

    return '';
  }, [emailExists, validationRules]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setValues(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = await validateField(name as keyof T, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const isValid = useCallback(() => {
    const requiredFields = Object.entries(validationRules)
      .filter(([_, rules]) => rules?.required)
      .map(([field]) => field);

    const hasAllRequired = requiredFields.every(field => values[field]);
    const hasNoErrors = Object.values(errors).every(error => !error);
    const allTouched = requiredFields.every(field => touched[field]);

    return hasAllRequired && hasNoErrors && allTouched && !isChecking;
  }, [values, errors, touched, isChecking, validationRules]);

  return {
    values,
    errors,
    touched,
    handleChange,
    isValid,
    setTouched
  };
}